import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation, useOutletContext } from "react-router-dom";
import type { FixedPhoneActivationParentOutletContext } from "../utils/fixed-phone-activation-outlet-context";
import { useDigitalSecurityPlansSlice } from "app/pages/Customer/Landing/DigitalSecurity/slice";
import {
  buildActivationCheckoutLocationState,
  getActivationFlowData,
  saveActivationFlowData,
} from "app/pages/Customer/FixedPhone/Checkout/utils/fixed-phone-storage";
import {
  isActivationSimYearlyPlan,
  isActivationServicePlanSeededForFlow,
} from "../utils/activation-plan-helpers";
import { useToast } from "app/components/ui/use-toast";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "app/components/ui/tabs";
import { Collapsible, CollapsibleContent } from "app/components/ui/collapsible";
import { Skeleton } from "app/components/ui/skeleton";
import { Button } from "app/components/ui/button";

/** Monthly/yearly security tabs follow choose-service-plan (and yearly SIM = yearly-only). */
const resolveSecurityTabFromFlow = (): "monthly" | "yearly" => {
  const data = getActivationFlowData();
  if (isActivationSimYearlyPlan(data?.simData)) return "yearly";
  return data?.selectedPlanType === "yearly" ? "yearly" : "monthly";
};

/** Below lg, stack order: Complete Individual → Complete Family → Basic Individual → Basic Family. At lg+, keep API order. */
const getActivationSecurityPlanStackOrder = (planName: string): number => {
  const n = (planName || "").toLowerCase();
  if (n.includes("complete individual")) return 1;
  if (n.includes("complete family")) return 2;
  if (n.includes("basic individual")) return 3;
  if (n.includes("basic family")) return 4;
  return 99;
};

const sortPlansForBelowLgStack = (plans: any[]) =>
  [...plans].sort(
    (a, b) =>
      getActivationSecurityPlanStackOrder(a?.planName) -
      getActivationSecurityPlanStackOrder(b?.planName),
  );

/** Match legacy mock headers for Complete plans; API header used for Basic plans. */
const getBannerHeaderText = (identity: any): string => {
  const name = (identity?.planName || "").toLowerCase();
  if (name.includes("complete individual")) {
    return "Identity, Fraud & Child Protection";
  }
  if (name.includes("complete family")) {
    return "Identity, Fraud & Child Protection For 10 Adults and Unlimited Kids";
  }
  return String(identity?.header || "").replace(/\n/g, " ");
};

export const SecurityPlanFreeTrial = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const securityYearlyOnly = isActivationSimYearlyPlan(
    getActivationFlowData()?.simData,
  );
  const [tab, setTab] = useState<"monthly" | "yearly">(() =>
    resolveSecurityTabFromFlow(),
  );
  const { useLazyGetDigitalSecurityPlansQuery } =
    useDigitalSecurityPlansSlice();
  const [
    getDigitalSecurityPlans,
    {
      isLoading,
      isFetching,
      data,
      isError,
      isUninitialized,
    },
  ] = useLazyGetDigitalSecurityPlansQuery();

  const identityPlans = data?.data?.data;

  /** True while a request is in flight (prod: slow networks must not use stale/empty data for clicks). */
  const isRequestPending = isLoading || isFetching;

  /**
   * Only allow trial CTAs after the current request has finished successfully with a payload.
   * Avoids navigating on uninitialized state or while a fetch is still resolving.
   */
  const canUsePlanActions =
    !isRequestPending && !isError && data !== undefined;
  const [openStates, setOpenStates] = useState<{ [key: number]: boolean }>({});
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );
  const displayIdentityPlans = useMemo(() => {
    const list = identityPlans || [];
    if (!list.length) return list;
    if (windowWidth >= 1024) return list;
    return sortPlansForBelowLgStack(list);
  }, [identityPlans, windowWidth]);

  useEffect(() => {
    if (
      location.pathname !==
      "/activation/fixed-phone/security-plan-free-trial"
    )
      return;
    const nextTab = resolveSecurityTabFromFlow();
    setTab(nextTab);
    getDigitalSecurityPlans({ type: nextTab });
  }, [location.pathname, location.key, getDigitalSecurityPlans]);

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const isNowAboveSm = newWidth >= 640;
      setWindowWidth(newWidth);

      if (isNowAboveSm && identityPlans) {
        setOpenStates((prev) => {
          const newStates = { ...prev };
          identityPlans.forEach((_: unknown, index: number) => {
            newStates[index] = true;
          });
          return newStates;
        });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [identityPlans]);

  useEffect(() => {
    if (displayIdentityPlans.length && Object.keys(openStates).length === 0) {
      const shouldBeOpen = windowWidth >= 640;
      const newOpenStates: { [key: number]: boolean } = {};
      displayIdentityPlans.forEach((_: unknown, index: number) => {
        if (shouldBeOpen) {
          newOpenStates[index] = true;
        } else {
          newOpenStates[index] = index === 0;
        }
      });
      setOpenStates(newOpenStates);
    }
  }, [displayIdentityPlans, windowWidth, openStates]);

  const handleTabChange = (value: string) => {
    if (securityYearlyOnly) return;
    const nextTab = value as "monthly" | "yearly";
    if (nextTab === tab) return;
    setTab(nextTab);
    getDigitalSecurityPlans({ type: nextTab });
  };

  const trialNavigationLockRef = useRef(false);

  const handleAcceptOffer = useCallback(
    (identity: any) => {
      if (trialNavigationLockRef.current) return;
      if (!canUsePlanActions) return;

      const securityPlanId = String(identity?._id || "").trim();
      if (!securityPlanId) return;

      trialNavigationLockRef.current = true;
      try {
        saveActivationFlowData({
          optForSecurityPlan: true,
          securityPlanId,
        });
        const activation = getActivationFlowData();
        if (!isActivationServicePlanSeededForFlow(activation)) {
          toast({
            title: "Select a service plan first",
            description:
              "Your service plan couldn’t be loaded earlier. Choose a monthly or annual home phone plan, then return here to add Identity Protection.",
            variant: "error",
          });
          navigate("/activation/fixed-phone/choose-service-plan");
          trialNavigationLockRef.current = false;
          return;
        }
        const selectedPlanType = activation?.selectedPlanType;
        if (selectedPlanType === "monthly") {
          navigate("/activation/fixed-phone/one-time-offer");
          return;
        }
        navigate("/checkout", {
          state: buildActivationCheckoutLocationState(),
        });
      } catch {
        trialNavigationLockRef.current = false;
      }
    },
    [canUsePlanActions, navigate, toast],
  );

  /** Matches loading skeletons: any in-flight or not-yet-loaded state after mount. */
  const isLoadingEffective =
    isUninitialized || isRequestPending;

  const showEmptyPlans =
    !isLoadingEffective &&
    !isError &&
    data !== undefined &&
    (identityPlans?.length ?? 0) === 0;

  const { setActivationFlowFooterSuppressed } =
    useOutletContext<FixedPhoneActivationParentOutletContext>() ?? {};

  useEffect(() => {
    const suppressed = isError || showEmptyPlans;
    setActivationFlowFooterSuppressed?.(suppressed);
    return () => setActivationFlowFooterSuppressed?.(false);
  }, [isError, showEmptyPlans, setActivationFlowFooterSuppressed]);

  return (
    <div className="w-full">
      <div className="text-center lg:px-5 mb-4 sm:mb-[1.125rem] lg:mb-5">
        <h2 className="font-mobile text-[#1D1D1B] text-[clamp(1.5rem,calc(2vw+1rem),2.25rem)] font-normal leading-snug md:leading-normal lg:leading-[3.3125rem] mb-1 sm:mb-1.5 lg:mb-2">
          Free Online Security &amp; Identity Protection
        </h2>
        <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] md:text-base lg:text-lg font-normal leading-[1.125rem] sm:leading-5 md:leading-6 lg:leading-7">
          Try any Aura plan free for 14 days. All annual plans include a 60-day
          money-back guarantee.
        </p>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange}>
        {!securityYearlyOnly && (
          <TabsList className="flex gap-3 sm:gap-4 md:gap-5 w-[17.5rem] md:w-[21.25rem] lg:w-[23.25rem] h-12 sm:h-[3.25rem] md:h-14 lg:h-[3.75rem] p-0 mt-3 sm:mt-4 md:mt-5 mb-6 sm:mb-8 md:mb-10 lg:mb-12 border border-[#D2D2D2] rounded-[5.75rem] mx-auto overflow-hidden">
            <TabsTrigger
              value="monthly"
              className={`${
                tab === "monthly" ? "!text-[#FE4D01]" : "!text-black"
              } relative z-[1] !bg-transparent !shadow-none p-0 text-center text-[0.825rem] sm:text-sm md:text-base lg:text-lg font-semibold leading-tight sm:leading-snug md:leading-normal`}
            >
              Monthly
            </TabsTrigger>
            <div
              className={`relative flex items-center bg-[#FBFBFB] w-[4.1875rem] md:w-[4.6875rem] lg:w-[5.1875rem] h-[1.75rem] md:h-[2.075rem] lg:h-[2.25rem] p-[0.175rem] md:p-1 border border-[#E5E5E5] rounded-[1.125rem] cursor-pointer ${
                tab === "monthly" ? "" : "justify-end"
              }`}
              onClick={() =>
                handleTabChange(tab === "monthly" ? "yearly" : "monthly")
              }
            >
              <div className="absolute z-[1] inset-0 bg-[#FBFBFB] rounded-full" />
              <div
                className={`absolute w-[15.1875rem] h-[2.6875rem] md:h-[3.1875rem] lg:h-[3.6875rem] ${
                  tab === "monthly"
                    ? "top-[-0.455rem] md:top-[-0.5585rem] lg:top-[-0.715rem] right-[0.0375rem] md:right-[1.395rem] lg:right-[2.59375rem] rotate-180"
                    : "top-[-0.5675rem] md:top-[-0.6575rem] lg:top-[-0.835rem] left-[0.0375rem] md:left-[1.395rem] lg:left-[2.59375rem]"
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src="/assets/gradient-shadow.svg"
                  className="w-full h-full cursor-auto select-none"
                  draggable={false}
                  alt="Shadow"
                />
              </div>
              <div className="relative z-[1] flex justify-center items-center bg-[#FE4D01] w-1/2 h-full rounded-full">
                <div className="bg-white w-1.5 h-0.5 rounded-sm" />
              </div>
            </div>
            <TabsTrigger
              value="yearly"
              className={`${
                tab === "yearly" ? "!text-[#FE4D01]" : "!text-black"
              } relative z-[1] !bg-transparent !shadow-none p-0 text-center text-[0.825rem] sm:text-sm md:text-base lg:text-lg font-semibold leading-tight sm:leading-snug md:leading-normal`}
            >
              Yearly
            </TabsTrigger>
          </TabsList>
        )}
        {securityYearlyOnly && (
          <div
            className="mt-3 sm:mt-4 md:mt-5 mb-6 sm:mb-8 md:mb-10 lg:mb-12"
            aria-hidden
          />
        )}

        <TabsContent value={tab} className="mt-0">
          {isError && (
            <div className="flex w-full justify-center items-center p-4 py-8 sm:py-12 flex-1 h-[calc(100vh-35.5rem)] sm:h-[calc(100vh-22.75rem)] md:h-[calc(100vh-24.5rem)] lg:h-[calc(100vh-27.5rem)] max-h-[45rem]">
              <div className="flex flex-col justify-center items-center text-center">
                <h3 className="max-w-2xl font-mobile text-[#1D1D1B] text-xl sm:text-2xl lg:text-3xl font-bold leading-7 sm:leading-8 lg:leading-9 mb-2 sm:mb-2.5 lg:mb-3 mx-auto">
                  Failed to load free trial {tab} plans
                </h3>
                <p className="max-w-md text-[#1D1D1B]/80 text-sm sm:text-[0.925rem] lg:text-base font-normal leading-5 sm:leading-[1.375rem] lg:leading-6 mb-3.5 md:mb-4 lg:mb-5 mx-auto">
                  We couldn&apos;t retrieve the free trial plans at this moment.
                  Please try again.
                </p>
                <Button
                  type="button"
                  variant="primary"
                  className="h-10 sm:h-12 md:h-[3.25rem] lg:h-[3.375rem] px-5 sm:px-6 md:px-8 lg:px-10 w-fit rounded-full"
                  onClick={() => getDigitalSecurityPlans({ type: tab })}
                >
                  Try Again
                </Button>
              </div>
            </div>
          )}

          {showEmptyPlans && (
            <div className="flex w-full justify-center items-center p-4 py-8 sm:py-12 flex-1 h-[calc(100vh-35.5rem)] sm:h-[calc(100vh-22.75rem)] md:h-[calc(100vh-24.5rem)] lg:h-[calc(100vh-27.5rem)] max-h-[45rem]">
              <div className="flex flex-col justify-center items-center text-center">
                <h3 className="max-w-2xl font-mobile text-[#1D1D1B] text-xl sm:text-2xl lg:text-3xl font-bold leading-7 sm:leading-8 lg:leading-9 mb-2 sm:mb-2.5 lg:mb-3 mx-auto">
                  No free trial {tab} plans to show right now
                </h3>
                <p className="max-w-md text-[#1D1D1B]/80 text-sm sm:text-[0.925rem] lg:text-base font-normal leading-5 sm:leading-[1.375rem] lg:leading-6 mb-3.5 md:mb-4 lg:mb-5 mx-auto">
                  We didn&apos;t receive any plan options from our catalog. This
                  may be temporary—try refreshing. If it keeps happening, contact
                  support and we&apos;ll help you finish activation.
                </p>
                <Button
                  type="button"
                  variant="primary"
                  className="h-10 sm:h-12 md:h-[3.25rem] lg:h-[3.375rem] px-5 sm:px-6 md:px-8 lg:px-10 w-fit rounded-full"
                  onClick={() => getDigitalSecurityPlans({ type: tab })}
                >
                  Try Again
                </Button>
              </div>
            </div>
          )}

          {!isError && !showEmptyPlans && (
            <div className="w-full lg:px-5 lg:overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <div className="flex flex-wrap lg:flex-nowrap lg:justify-start gap-4 justify-center w-full md:gap-5 lg:w-max">
                {((!identityPlans || identityPlans.length === 0) &&
                isLoadingEffective
                  ? Array.from({ length: 4 })
                  : displayIdentityPlans || []
                )?.map((identityRaw: any, index: number) => {
                  const identity = identityRaw || {};
                  const nameLower = (
                    identity?.planName || "basic individual"
                  ).toLowerCase();
                  let strikePrice = 0;
                  if (nameLower.includes("basic individual")) strikePrice = 15;
                  else if (nameLower.includes("basic family")) strikePrice = 20;
                  else if (nameLower.includes("complete individual"))
                    strikePrice = 30;
                  else if (nameLower.includes("complete family"))
                    strikePrice = 50;

                  const isMonthly = tab === "monthly";
                  let displayPriceStr = "";
                  let calculatedSavings = 0;

                  const isSkeleton = isLoadingEffective;

                  if (identity?.price !== undefined) {
                    if (isMonthly) {
                      displayPriceStr = identity.price.toString();
                      if (strikePrice > 0)
                        calculatedSavings =
                          strikePrice - Number(identity.price);
                    } else {
                      const monthlyP = Number(identity.price) / 12;
                      displayPriceStr = Number.isInteger(monthlyP)
                        ? monthlyP.toString()
                        : monthlyP.toFixed(2);
                      if (strikePrice > 0)
                        calculatedSavings =
                          strikePrice * 12 - Number(identity.price);
                    }
                  } else {
                    displayPriceStr = "00";
                    calculatedSavings = 5;
                  }

                  const suffix = isMonthly ? "/ month" : "/ mo billed yearly";
                  const hasSavings = calculatedSavings > 0;

                  return (
                    <div
                      key={identity._id || index}
                      className="bg-white w-full lg:w-[21.5625rem] max-w-[21.5625rem] rounded-lg sm:rounded-xl overflow-hidden shadow-sm flex flex-col border border-[#EBEBEB]"
                    >
                      <div className="flex justify-center items-center bg-black h-11 sm:h-[2.825rem] md:h-[2.875rem] shrink-0">
                        <h3 className="max-w-[12.5rem] sm:max-w-52 text-[0.72rem] sm:text-xs font-semibold leading-4 text-center text-white">
                          {isSkeleton ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <Skeleton className="w-[13.75rem] sm:w-56 h-4 bg-white/20 rounded-md" />
                              {index === 3 && (
                                <Skeleton className="w-[9.75rem] sm:w-40 h-4 bg-white/20 rounded-md" />
                              )}
                            </div>
                          ) : (
                            getBannerHeaderText(identity)
                          )}
                        </h3>
                      </div>
                      <Collapsible
                        open={
                          windowWidth >= 640 ? true : openStates[index] || false
                        }
                        onOpenChange={(open) => {
                          if (windowWidth < 640) {
                            setOpenStates((prev) => ({
                              ...prev,
                              [index]: open,
                            }));
                          }
                        }}
                        className="flex flex-col flex-1 min-h-0"
                      >
                        <div className="p-4 sm:p-5 md:p-6 lg:p-[1.625rem] flex flex-col flex-1 min-h-0">
                          <div className="flex justify-between items-center">
                            <h4 className="text-black font-mobile text-[1.375rem] sm:text-2xl md:text-[1.625rem] font-medium leading-7 sm:leading-9 md:leading-[2.625rem] mb-2 sm:mb-3 md:mb-[0.815rem]">
                              {isSkeleton ? (
                                <Skeleton
                                  className={`${
                                    index < 2
                                      ? "w-[8.5rem] sm:w-36 md:w-40"
                                      : "w-48 sm:w-[12.5rem] md:w-[13.5rem]"
                                  } h-7 sm:h-9 md:h-[2.625rem] bg-[#E2E4E9] rounded-md`}
                                />
                              ) : (
                                <>
                                  <span className="sm:hidden">
                                    {(identity.planName || "")
                                      .split(" ")
                                      .reverse()
                                      .join(" ")}
                                  </span>
                                  <span className="hidden sm:inline">
                                    {identity.planName}
                                  </span>
                                </>
                              )}
                            </h4>
                            <div
                              className="mb-2 sm:hidden size-6 shrink-0"
                              onClick={() => {
                                if (windowWidth < 640) {
                                  setOpenStates((prev) => ({
                                    ...prev,
                                    [index]: !prev[index],
                                  }));
                                }
                              }}
                            >
                              <img
                                src="/assets/chevron-down-thick.svg"
                                alt=""
                                className={`size-full ${
                                  windowWidth >= 640 || openStates[index]
                                    ? "rotate-180"
                                    : ""
                                }`}
                              />
                            </div>
                          </div>
                          {isSkeleton ? (
                            <div className="flex gap-2 sm:gap-3 items-center py-1.5 md:py-2">
                              <div className="flex gap-1.5 sm:gap-2 items-end">
                                <Skeleton className="w-36 sm:w-[9.5rem] md:w-40 h-9 sm:h-10 md:h-11 bg-[#E2E4E9] rounded-md" />
                                <Skeleton
                                  className={`${
                                    tab === "monthly"
                                      ? "w-20 sm:w-[5.5rem]"
                                      : "w-[7.5rem] sm:w-32"
                                  } h-4 sm:h-5 md:h-6 bg-[#E2E4E9] rounded-md`}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 sm:gap-3">
                              {strikePrice > 0 && (
                                <p className="text-[#98A2B3] font-mobile text-4xl sm:text-[2.5rem] md:text-[2.75rem] font-normal leading-10 sm:leading-[2.75rem] md:leading-[3rem] tracking-[-0.06rem] line-through">
                                  ${strikePrice}
                                </p>
                              )}
                              <div className="flex gap-1">
                                <p className="text-[#101828] font-mobile text-4xl sm:text-[2.5rem] md:text-[2.75rem] font-bold leading-10 sm:leading-[2.75rem] md:leading-[3rem] tracking-[-0.06rem]">
                                  ${displayPriceStr}
                                </p>
                                <span className="inline-block text-[#667085] text-sm sm:text-[0.925rem] md:text-base font-medium leading-4 sm:leading-5 md:leading-6 mt-[1.125rem] whitespace-nowrap">
                                  {suffix}
                                </span>
                              </div>
                            </div>
                          )}
                          <CollapsibleContent className="flex flex-col flex-1 min-h-0">
                            <ul className="flex-1 space-y-2.5 sm:space-y-3 md:space-y-4 lg:space-y-5 my-4 sm:my-5 md:my-6 lg:my-7">
                              {isSkeleton
                                ? Array.from({ length: 5 }).map((_, i) => (
                                    <li
                                      key={i}
                                      className="flex items-start gap-[0.375rem] sm:gap-[0.45rem] md:gap-[0.56rem]"
                                    >
                                      <Skeleton className="shrink-0 w-4 h-4 sm:w-[1.125rem] sm:h-[1.125rem] md:w-5 md:h-5 bg-[#E2E4E9] rounded mt-[0.1rem] sm:mt-[0.2rem] md:mt-[0.1rem]" />
                                      <Skeleton className="w-40 sm:w-48 md:w-56 h-5 sm:h-6 bg-[#E2E4E9] rounded-md" />
                                    </li>
                                  ))
                                : (Array.isArray(identity.features) &&
                                  identity.features.length > 0
                                    ? identity.features
                                    : []
                                  ).map(
                                    (feature: string, featureIndex: number) => (
                                      <li
                                        key={featureIndex}
                                        className="flex items-start gap-[0.375rem] sm:gap-[0.45rem] md:gap-[0.56rem]"
                                      >
                                        <span className="shrink-0 mt-[0.1rem] sm:mt-[0.2rem] md:mt-[0.1rem] text-[#1EC677]">
                                          <svg
                                            className="w-4 h-4 sm:w-[1.125rem] sm:h-[1.125rem] md:w-5 md:h-5"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M2 12H22M22 12L15 7M22 12L15 17"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        </span>
                                        <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] md:text-base font-normal leading-5 sm:leading-6 md:leading-relaxed">
                                          {feature}
                                        </p>
                                      </li>
                                    ),
                                  )}
                            </ul>
                            {isSkeleton ? (
                              <div className="mb-4 sm:mb-5 md:mb-6 lg:mb-7">
                                <Skeleton className="w-48 sm:w-52 h-4 sm:h-5 bg-[#E2E4E9] rounded-md" />
                              </div>
                            ) : (
                              hasSavings && (
                                <p className="text-black text-[0.825rem] sm:text-sm font-bold leading-4 sm:leading-5 md:leading-[1.4375rem] mb-4 sm:mb-5 md:mb-6 lg:mb-7">
                                  Save ${calculatedSavings} off the regular
                                  price
                                </p>
                              )
                            )}
                            {isSkeleton ? (
                              <Skeleton className="w-full h-11 sm:h-[2.875rem] md:h-12 lg:h-[3.125rem] bg-[#E2E4E9] rounded-full" />
                            ) : (
                              <Button
                                type="button"
                                variant="primary"
                                className="h-11 sm:h-[2.875rem] md:h-12 lg:h-[3.125rem] rounded-[2.4375rem] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
                                onClick={() => handleAcceptOffer(identity)}
                                disabled={
                                  !canUsePlanActions ||
                                  !String(identity?._id || "").trim()
                                }
                              >
                                Start 14-day Free Trial
                              </Button>
                            )}
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
